import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PlatformService } from '@core/services/platform.service';
import { DestroyableComponent } from '@models/destroyable.component';
import {
  Collection,
  CSchool,
  EContentPanelRoute,
  ESchoolParticipantRole,
  Language,
  Lesson,
  PublishStatusEnum,
  Subject,
  SubjectLevel,
  UserService,
  UserServiceCategory,
  UserServiceCategoryLibrary,
} from 'lingo2-models';
import { filter, takeUntil, tap, zip } from 'rxjs';
import { LanguageService } from '../language.service';
import { ConfigService } from '../lingo2-content/config.service';
import { DictionaryService, LanguageFromDictionary, SubjectFromDictionary } from './dictionary.service';
import {
  TeachingSubject,
  UserCollectionOfLessons,
  UserLesson,
  UserSchool,
  UserServiceBff,
} from './user-profile.service';

export function findSubjectById(subjects: Subject[], teaching: TeachingSubject): any {
  return subjects.find((_s) => +_s.id === +teaching?.subjectId);
}

export function subjectPseudoId(subject: TeachingSubject): string {
  return subject.subjectId.toString();
}

@Injectable({
  providedIn: 'root',
})
export class HelpersBffService extends DestroyableComponent {
  protected http = inject(HttpClient);
  protected dictionaryService = inject(DictionaryService);
  protected configService = inject(ConfigService);
  protected languageService = inject(LanguageService);
  protected readonly platform: PlatformService;

  protected usCategories: UserServiceCategory[] = [];
  protected languages: LanguageFromDictionary[] = [];
  protected subjects: SubjectFromDictionary[] = [];
  protected currentLanguage: Language;
  protected subjectLevels: SubjectLevel[] = [];

  constructor() {
    super();

    zip([
      this.dictionaryService.uscategories,
      this.dictionaryService.languages,
      this.dictionaryService.subjects,
      this.languageService.language$,
    ])
      .pipe(
        tap(([categories, languages, subjects, currentLanguage]) => {
          this.usCategories = categories;
          this.languages = languages;
          this.subjects = subjects;
          this.currentLanguage = currentLanguage;
        }),
      )
      .subscribe();

    this.configService.subjectLevels2$
      .pipe(
        filter((subjectLevels) => !!subjectLevels),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (subjectLevels) => {
          this.subjectLevels = Object.keys(subjectLevels).map(function (subject) {
            const tempSubj = subjectLevels[subject];
            return tempSubj;
          })[0];
        },
      });
  }

  public convertBffLessonsToLegacy(lessons: UserLesson[]): Lesson[] {
    return lessons.map(
      (lesson) =>
        new Lesson({
          id: lesson._id,
          title: lesson.title,
          description: lesson.description,
          subject_id: lesson.subjectId,
          subject: {
            id: lesson.subjectId,
            code: this.subjects.find((sbjct) => sbjct._id === lesson.subjectId)?.code || null,
            title: this.subjects.find((sbjct) => sbjct._id === lesson.subjectId)?.title || null,
            category_id: this.subjects.find((sbjct) => sbjct._id === lesson.subjectId)?.categoryId || null,
            language_id: this.subjects.find((sbjct) => sbjct._id === lesson.subjectId)?.languageId || null,
            levels_category_id: this.subjects.find((sbjct) => sbjct._id === lesson.subjectId)?.levelCategoryId || null,
          },
          slug: lesson.slug,
          level_id: lesson.levelId,
          level: this.subjectLevels.find((level) => level.id === lesson.levelId) || null,
          cover_id: lesson.coverId,
          language_id: this.languages.find((lang) => lang.code === lesson.languageCode)?._id || 0,
          published_at: new Date(lesson.publishedAt),
          visit_info: {
            is_favorite: lesson.visitInfo.isBookmarked,
            is_liked: lesson.visitInfo.isLiked,
            is_disliked: lesson.visitInfo.isDisliked,
            is_viewed: lesson.visitInfo.isViewed,
          },
          stats: {
            views_count: lesson.numberOfViews,
          },
          type: lesson.type as any,
        }),
    );
  }

  public convertBffSchoolsToLegacy(schools: UserSchool[]): CSchool[] {
    return schools.map(
      (school) =>
        new CSchool({
          id: school._id,
          title: school.title,
          short_description: school.subDescription,
          description: school.description,
          slug: school.slug,
          status: school.status,
          head_cover_id: school.headCoverId,
          cover_id: school.coverId,
          owners: school.owners.map((owner) => ({
            id: owner.accountId,
            school_id: school._id,
            account_id: owner.accountId,
            userpic_id: owner.avatarId,
            role: ESchoolParticipantRole.owner,
            own_profit: 0,
            created_at: new Date(),
            updated_at: new Date(),
          })),
          teachers: school.teachers.map((teacher) => ({
            id: teacher.accountId,
            school_id: school._id,
            account_id: teacher.accountId,
            userpic_id: teacher.avatarId,
            role: ESchoolParticipantRole.teacher,
            own_profit: 0,
            created_at: new Date(),
            updated_at: new Date(),
          })),
          participants: school.students.map((student) => ({
            id: student.accountId,
            school_id: school._id,
            account_id: student.accountId,
            userpic_id: student.avatarId,
            role: ESchoolParticipantRole.student,
            own_profit: 0,
            created_at: new Date(),
            updated_at: new Date(),
          })),
          subjects:
            school.subjects?.length > 0
              ? school.subjects.map(
                  (subject) =>
                    new Subject({
                      id: subject.subjectId,
                      code: subject.subjectName,
                      title: subject.subjectName,
                      category_id: 1,
                      language_id: 1,
                    }),
                )
              : [],
          created_at: new Date(),
          updated_at: new Date(),
          visit_info: {
            is_favorite: school.visitInfo.isBookmarked,
            is_viewed: false,
          },
          stats: {
            meeting_count: 0,
            participant_count: 0,
          },
          review_count: 0,
        }),
    );
  }

  public convertBffCollectionsToLegacy(collections: UserCollectionOfLessons[]): Collection[] {
    return collections.map(
      (collection) =>
        new Collection({
          id: collection._id,
          title: collection.title,
          excerpt: collection.excerpt,
          description: collection.description,
          price_tiers: collection.priceTiers,
          slug: collection.slug,
          type: collection.type,
          cover_id: collection.multiCoverId[0],
          multi_cover_id: collection.multiCoverId,
          author_id: collection.authorId,
          subject_id: collection.subjectId,
          subject: {
            id: collection.subjectId,
            code: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).code,
            title: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).title,
            category_id: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).categoryId,
            language_id: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).languageId,
            levels_category_id: this.subjects.find((sbjct) => sbjct._id === collection.subjectId).levelCategoryId,
          },
          discount: collection.discount,
          keywords: [collection.excerpt],
          items: collection?.items.map((item) => ({
            id: item.lessonId,
            collection_id: item.collectionId,
            content_id: item.contentId,
            sort_order: item.sortOrder,
            is_open: item.isOpen,
          })),
          stats: {
            total: collection.numberOfTotal,
            // views_count: number;
            // reviews_count: number;
            // rating: number;
            // likes_count: number;
            // dislikes_count: number;
            // shares_count: number;
          },
          // options: {
          //   open_lessons: collection.;
          // };
          publish_status: PublishStatusEnum.published,
          changed_at: (collection.changedAt as any) || Date.now(),
          moderation_status: collection.moderationStatus,
          moderation_message: collection.moderationMessage,
          // visit_info: {
          //   licensed: collection.visitInfo.
          // }
          // promotion: collection;
          // promotion_status: PromotionStatusEnum;
          // advertisement: collection;
          // advertisement_status: AdvertisementStatusEnum;
          // access: CollectionAccessEnum;
          purl: collection.purl,
          // created_at: collection;
          // updated_at: Date;
          // hidden?: boolean;
          // valid?: boolean;
          // readonly?: boolean;
          // can: {
          //     edit = "edit",
          //     buy = "buy",
          //     upgrade = "upgrade",
          //     review = "review",
          //     publish = "publish",
          //     unpublish = "unpublish",
          //     archive = "archive",
          //     restore = "restore"
          // };
          // is: {
          //   draft = "draft",
          //   archived = "archived",
          //   on_moderation = "on_moderation",
          //   approved = "approved",
          //   declined = "declined"
          // };
        }),
    );
  }

  /** Полная ссылка на страницу индивидуального занятия */
  public userServiceRouteUniversal(item): string {
    if (item) {
      const subjectCode = this.subjects.find((el) => el._id === item.subject_id)?.code || item.subject?.code || '-';
      const typeCode = UserServiceCategoryLibrary.findByPk(item?.category_id)?.code || item.category?.code || '-';
      const title = (item.title || '')
        .replace(/[\\\/:;{}\[\]()_,-.!?'"`+#&=]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      return [
        '/' + subjectCode,
        EContentPanelRoute.classes,
        typeCode,
        (title.length ? title + '-' : '') + item.slug,
      ].join('/');
    } else {
      return null;
    }
  }
}
